'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';

const UTM_STORAGE_KEY = 'zestygen_utm';
const UTM_EXPIRY_DAYS = 30;

export interface UTMData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  timestamp?: number;
}

type UTMParamKey = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content';

const UTM_PARAMS: UTMParamKey[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * Hook to capture and store UTM parameters from URL
 * Call this in your root layout to track marketing attribution
 */
export function useUTMTracking() {
  const searchParams = useSearchParams();

  useEffect(() => {
    // Check if URL has UTM parameters
    const utmData: UTMData = {};
    let hasUTM = false;

    UTM_PARAMS.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUTM = true;
      }
    });

    // If has UTM params, save to localStorage
    if (hasUTM) {
      utmData.landing_page = window.location.pathname;
      utmData.timestamp = Date.now();
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    }
  }, [searchParams]);
}

/**
 * Get stored UTM data from localStorage
 * Returns null if no UTM data or if expired (30 days)
 */
export function getStoredUTM(): UTMData | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return null;

    const utmData: UTMData = JSON.parse(stored);

    // Check if expired (30 days)
    if (utmData.timestamp) {
      const expiryTime = UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() - utmData.timestamp > expiryTime) {
        localStorage.removeItem(UTM_STORAGE_KEY);
        return null;
      }
    }

    return utmData;
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM data
 * Call this after successful payment to avoid duplicate attribution
 */
export function clearStoredUTM() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(UTM_STORAGE_KEY);
}

